/**
 * The `scan-cards` plugin, from the web layer's side: what the phone sends, and what
 * this does with it.
 *
 * The split is the same one the browser has with the API (ADR 0009), moved onto the
 * device - and it is deliberately lopsided, because only two things actually need to
 * be native:
 *
 *  - **the camera**, which is a phone capability and nothing else;
 *  - **a real OCR engine**, which is a native model (ML Kit) and the one part of the
 *    cascade this repository has never had (ADR 0007 built the port with two
 *    deterministic doubles and no engine).
 *
 * Everything else the phone appears to do is done here, by `packages/shared`: the
 * plugin hands over grey levels, and `readCapture` finds the card, rectifies it,
 * classifies its era, cuts the constant art window and fingerprints it. That is the
 * *same function* the browser camera path runs, so the phone cannot drift from the
 * benchmark the window and the threshold were measured on (ADR 0006, ADR 0008) - and
 * there is no Kotlin copy of a rectangle, a DCT or an era table to keep in step.
 *
 * So the plugin's contract is small on purpose: give me pixels at a width, give me
 * text for a frame id, tell me whether you can do either. Everything else is shared
 * code that runs identically on a phone, in a browser and in a test.
 */

import { type FrameClass, type FrameImage, type OcrPort, type OcrReading, type OcrRequest } from '@unsorted/shared';
import { fromBase64 } from '../base64.js';
import { FRAME_WIDTH, readCapture } from './capture.js';

/** What a successful capture looks like coming back over the bridge. */
export interface NativeCaptureMessage {
  /** Identifies the frame the plugin is holding, for the OCR read that follows. */
  frameId: string;
  width: number;
  height: number;
  /** Grey levels, base64: one byte per pixel, `width * height` of them. */
  levels: string;
  /** What the camera found, in its own words - a framing hint, a rotation, a glare. */
  notes?: string[];
}

/**
 * What the phone's live analysis loop reports, several times a second.
 *
 * These are **physics, not a detector**: the mean light level, how much texture the
 * frame carries, and how much it changed since the last look. The shell computes
 * them from the camera's luma plane and nothing else - no corners, no card, no art
 * window, no fingerprint - so the measured geometry stays in `packages/shared` and
 * there is no second detector to disagree with it (ADR 0014). Deciding what those
 * numbers mean is `observationFromLiveFrame`, in `native-camera.ts`.
 */
export interface LiveFrameMessage {
  /** Mean luma of the analysed frame, 0..255. */
  luma: number;
  /** Mean absolute neighbour difference, in levels: the scene's own texture. */
  edge: number;
  /** Mean absolute change from the previous analysed frame, in levels. */
  motion: number;
}

/** What a live session answers with when the camera is bound and previewing. */
export interface LiveStartMessage {
  /** Width of the grey still `captureLive` answers with. */
  width: number;
  /** Whether this camera has a torch the sheet may offer. */
  torch: boolean;
  engine?: string;
}

/**
 * The live half of the plugin contract.
 *
 * `startLive` binds the camera **once** and leaves it previewing; `captureLive`
 * takes the still the live loop asks for. That split is the fix for the old path,
 * which unbound and rebound the camera for every single frame.
 */
export interface LiveScanCardsPlugin extends ScanCardsPlugin {
  startLive(options?: { width?: number }): Promise<LiveStartMessage>;
  stopLive(): Promise<void>;
  captureLive(): Promise<NativeCaptureMessage>;
  setTorch(options: { on: boolean }): Promise<void>;
  focusAt(options: { x: number; y: number }): Promise<void>;
  addListener(
    event: 'liveFrame',
    listener: (frame: LiveFrameMessage) => void,
  ): Promise<{ remove(): Promise<void> }>;
}

export interface ScanCardsPlugin {
  /** Whether this device can scan at all, and with which engine, before it tries. */
  availability(): Promise<{ available: boolean; engine?: string; reason?: string }>;
  capture(options: { width: number }): Promise<NativeCaptureMessage>;
  readFooter(options: {
    frameId: string;
    rect: { x0: number; y0: number; x1: number; y1: number };
    frameClass: FrameClass;
    face: 'front' | 'back';
  }): Promise<{ text: string; confidence: number; engine: string }>;
}

/** A capture, read by the shared core, with the device's own bookkeeping attached. */
export interface DeviceScan {
  /** False when the plugin cannot run here: no camera, no permission, no engine. */
  available: boolean;
  /** Why not, in the plugin's own words. */
  reason: string | null;
  /** The frame the plugin holds, which is what an OCR read is about. */
  frameId: string | null;
  /** The card on its own coordinates, or null when the capture held no card. */
  frame: FrameImage | null;
  frameClass: FrameClass;
  frameConfidence: number;
  /** The card's share of the capture, as the detector's own confidence. */
  coverage: number;
  /** What the core and the camera found, in their own words. */
  notes: string[];
  /** The fingerprint the server matches, or null when there was no card to hash. */
  artPhash: string | null;
  /** Which native engine produced the frame, for a report or a diagnostics screen. */
  engine: string | null;
}

const unavailable = (reason: string): DeviceScan => ({
  available: false,
  reason,
  frameId: null,
  frame: null,
  frameClass: 'unknown',
  frameConfidence: 0,
  coverage: 0,
  notes: [],
  artPhash: null,
  engine: null,
});

/**
 * A message's pixels as a frame, or null with a reason.
 *
 * The plugin's geometry is checked rather than trusted: a frame whose dimensions
 * disagree with how many bytes arrived is not a shape the core's functions accept,
 * and letting `assertGreyImage` throw would turn a bad bridge message into a failed
 * scan. A capture is a finding either way.
 */
export function decodeFrame(message: NativeCaptureMessage): { frame: FrameImage | null; note: string | null } {
  const expected = message.width * message.height;
  if (!Number.isInteger(expected) || expected <= 0) {
    return { frame: null, note: `the device sent a frame of ${message.width}x${message.height}` };
  }
  const levels = fromBase64(message.levels);
  if (levels.byteLength !== expected) {
    return {
      frame: null,
      note: `the device sent ${levels.byteLength} bytes for a ${message.width}x${message.height} frame`,
    };
  }
  return { frame: { width: message.width, height: message.height, levels }, note: null };
}

/**
 * One capture from the device, read by the shared core.
 *
 * Never throws on a plugin that is missing or broken: the caller gets something it can
 * render, and the scan surface falls back to the camera in the browser or to typing
 * the card in.
 */
export async function deviceCapture(
  plugin: ScanCardsPlugin,
  options: { width?: number } = {},
): Promise<DeviceScan> {
  let availability: { available: boolean; engine?: string; reason?: string };
  try {
    availability = await plugin.availability();
  } catch (error) {
    return unavailable(
      `the camera plugin could not be asked (${error instanceof Error ? error.message : String(error)})`,
    );
  }
  if (!availability.available) {
    return unavailable(availability.reason ?? 'this device cannot scan cards');
  }

  let message: NativeCaptureMessage;
  try {
    message = await plugin.capture({ width: options.width ?? FRAME_WIDTH });
  } catch (error) {
    return unavailable(`the capture failed (${error instanceof Error ? error.message : String(error)})`);
  }

  const notes = [...(message.notes ?? [])];
  const decoded = decodeFrame(message);
  if (decoded.note !== null) notes.push(decoded.note);

  // The core reads the capture: find the card, rectify it, classify the era, cut and
  // hash the art window. One implementation, the one every number was measured with.
  const read = decoded.frame === null ? null : readCapture(decoded.frame);

  return {
    available: true,
    reason: null,
    frameId: message.frameId,
    frame: read?.frame ?? null,
    frameClass: read?.frameClass ?? 'unknown',
    frameConfidence: read?.frameConfidence ?? 0,
    coverage: read?.coverage ?? 0,
    notes: [...notes, ...(read?.notes ?? [])],
    artPhash: read?.artPhash ?? null,
    engine: availability.engine ?? null,
  };
}

/**
 * The footer engine, as the port the cascade already knows how to ask.
 *
 * The port is bound to one capture, and refuses to read any other frame. That refusal
 * is the point: `resolveScan` hands back the frame it is resolving, and the native side
 * can only read the frame it last captured, so answering a request about a *different*
 * frame would mean silently reading pixels from a buffer that is not the card being
 * identified. Refusing costs a fallback to the artwork; answering wrongly costs a card
 * filed as the wrong printing.
 */
export function footerOcrPort(plugin: ScanCardsPlugin, scan: DeviceScan): OcrPort {
  return {
    async read(request: OcrRequest): Promise<OcrReading> {
      if (scan.frame === null || scan.frameId === null) {
        return { text: '', confidence: 0, engine: 'no-capture' };
      }
      if (request.frame !== scan.frame) {
        return { text: '', confidence: 0, engine: 'not-this-frame' };
      }
      try {
        const reading = await plugin.readFooter({
          frameId: scan.frameId,
          rect: request.rect,
          frameClass: request.frameClass,
          face: request.face,
        });
        return { text: reading.text, confidence: reading.confidence, engine: reading.engine };
      } catch (error) {
        // An engine that is not there is not a failed scan: the cascade still has the
        // artwork, and a reading of nothing is how it is told so.
        return {
          text: '',
          confidence: 0,
          engine: `unavailable: ${error instanceof Error ? error.message : String(error)}`,
        };
      }
    },
  };
}
